<script setup>
import LoadingSpinner from "@/Components/LoadingSpinner.vue";
import {computed, onMounted, ref} from "vue";
import EmojiPicker from "@/Components/EmojiPicker.vue";
import {useMergeFieldDragAndDropHandler, useInitDragAndDropFields} from "@/utils/useMergeFieldDragAndDropHandler";
import { PhotographIcon, XIcon } from "@heroicons/vue/solid";
import axios from "axios";

const props = defineProps({
  leadCategory: {required: true, type: String,},
  type: Boolean,
  formData: Object,
  loading: Boolean,
  prepended_text: String,
  appended_text: String,
  errors: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['submit']);
const imageInputRef = ref(null);
const uploadingImages = ref(false);
const previewUrls = ref([]);

const previewMessage = computed(() => {
  return `${props.prepended_text} ${props.formData?.message ?? '[MESSAGE]'} ${props.appended_text}`;
})

function isInvalid(attribute) {
  if (props.formData.errors && props.formData.errors[attribute]) {
    return true;
  } else {
    return props.errors && props.errors.hasOwnProperty(attribute);
  }
}

function errorMessage(attribute) {
  if (props.formData.errors && props.formData.errors[attribute]) {
    return props.formData.errors[attribute];
  } else if (props.errors && props.errors.hasOwnProperty(attribute)) {
    return props.errors[attribute][0];
  }
}

function openImagePicker() {
  imageInputRef.value.click();
}

async function handleFileUpload(event) {
  const files = event.target.files;
  if (!files.length) return;
  
  // Validate file count (max 10 total)
  if (previewUrls.value.length + files.length > 10) {
    alert("Maximum 10 images allowed per message");
    return;
  }

  // Validate file types and sizes
  const MAX_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  
  const invalidFiles = Array.from(files).filter(file => {
    if (file.size > MAX_SIZE) {
      alert(`File "${file.name}" is too large. Maximum size is 5MB.`);
      return true;
    }
    
    if (!ALLOWED_TYPES.includes(file.type)) {
      alert(`File "${file.name}" is not a supported image type. Use JPEG, PNG, GIF or WebP.`);
      return true;
    }
    
    return false;
  });
  
  if (invalidFiles.length > 0) {
    event.target.value = '';
    return;
  }

  uploadingImages.value = true;

  try {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('images[]', file);
    });

    const response = await axios.post('/api/mms/upload-images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      }
    });

    if (response.data.success) {
      // Add uploaded images to previewUrls
      response.data.images.forEach(image => {
        previewUrls.value.push({
          url: image.url,
          name: image.original_name,
          type: image.mime_type
        });
      });
      
      // Update form's file_media field
      updateFormFileMedia();
    }
  } catch (error) {
    console.error('Failed to upload images:', error);
    alert("Failed to upload images. Please try again.");
  } finally {
    uploadingImages.value = false;
    // Clear the input so the same file can be selected again
    event.target.value = '';
  }
}

function removeFile(index) {
  previewUrls.value.splice(index, 1);
  updateFormFileMedia();
}

function updateFormFileMedia() {
  if (!props.formData.file_media) {
    props.formData.file_media = [];
  }
  
  props.formData.file_media = previewUrls.value.map(item => ({
    mediaUrl: item.url,
    mimeType: item.type
  }));
}

onMounted(() => {
    useInitDragAndDropFields();
    
    // Initialize form.file_media if it doesn't exist
    if (!props.formData.file_media) {
      props.formData.file_media = [];
    }
    
    // If form already has file_media, load it into previewUrls
    if (props.formData.file_media && props.formData.file_media.length > 0) {
      props.formData.file_media.forEach(item => {
        previewUrls.value.push({
          url: item.mediaUrl,
          name: 'Uploaded image',
          type: item.mimeType
        });
      });
    }
})

const modelName = computed(() => {
  switch (props.leadCategory) {
    case 'recruitment':
      return 'Recruitment Schedule';
    case 'commercial':
    default:
      return 'Action Schedule';
  }
});
const isCommercialLead = computed(() => { // Commercial Lead: classic lead. The other type is a recruitment lead
  return !props.leadCategory || props.leadCategory !== 'recruitment';
});
</script>

<template>
  <form @submit.prevent="emit('submit')" class="intro-y box bg-white p-5">
    <div class="grid grid-cols-12 gap-3 lg:gap-6 mt-3">
      <!-- BEGIN: Type -->
      <div class="col-span-12 input-form" :class="{'has-error': isInvalid('type') }">
        <label for="type" class="form-label text-gray-600">Type</label>
        <select v-model="formData.type" class="input-field" name="" id="">
          <option v-if="type" value="Agency">Agency</option>
          <option value="Agent">Agent</option>
        </select>
        <div class="error-message mt-1" v-if="isInvalid('type')">{{ errorMessage('type') }}</div>
      </div>
      <!-- END: Type -->

      <!-- BEGIN: Name -->
      <div class="col-span-12 input-form" :class="{'has-error': isInvalid('name')}">
        <label for="name" class="form-label text-gray-600">Template Name</label>
        <input v-model="formData.name" id="name" type="text" class="input-field"
               name="name">
        <div class="error-message mt-1" v-if="isInvalid('name')">{{ errorMessage('name') }}</div>
      </div>
      <!-- END: Name -->

      <div class="grid grid-cols-12 gap-3 lg:gap-6 mt-3 border-b border-slate-100 pb-6 col-span-12">
        <!-- BEGIN: Text Message -->
        <div class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('message')}">
          <label for="text-template-message-form-field" class="form-label text-s text-slate-600">Message</label>
          
          <!-- Image Attachments Preview -->
          <div v-if="previewUrls.length > 0" class="mb-3">
            <div class="flex flex-wrap items-center gap-2">
              <div v-for="(preview, index) in previewUrls" :key="index" class="relative">
                <img 
                  :src="preview.url" 
                  :alt="preview.name"
                  class="h-20 w-20 object-cover rounded-lg border border-gray-200"
                />
                <button
                  @click="removeFile(index)"
                  class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors"
                  type="button"
                >
                  <XIcon class="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          
          <div class="relative flex">
            <span class="absolute left-0 flex items-center justify-center rounded-full h-10 w-10 bg-gray-200 m-2">
              <emoji-picker input-element-id="text-template-message-form-field" :text="formData.message" @emoji-selected="(e) => formData.message = e.updatedText"></emoji-picker>
            </span>
            
            <!-- Image Upload Button -->
            <button
              @click="openImagePicker"
              :disabled="previewUrls.length >= 10 || uploadingImages"
              class="absolute left-12 inline-flex items-center justify-center rounded-full h-10 w-10 transition duration-500 ease-in-out text-gray-500 bg-gray-200 hover:bg-gray-300 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed m-2"
              type="button"
              title="Attach images (MMS)"
            >
              <div v-if="uploadingImages" class="animate-spin h-5 w-5 border-2 border-gray-500 border-t-transparent rounded-full"></div>
              <PhotographIcon v-else class="h-6 w-6 text-gray-600" />
            </button>
            
            <textarea 
              @dragover.prevent 
              @drop.prevent="props.formData.message = useMergeFieldDragAndDropHandler($event)" 
              contenteditable="true" 
              tabindex="0" 
              v-model="formData.message" 
              name="message" 
              id="text-template-message-form-field" 
              maxlength="6000" 
              rows="6" 
              cols="50" 
              type="text" 
              class="input-field w-full pl-28"
            ></textarea>
            
            <!-- Hidden file input -->
            <input
              ref="imageInputRef"
              type="file"
              multiple
              accept="image/*"
              class="hidden"
              @change="handleFileUpload"
            />
          </div>
          <div class="error-message mt-1" v-if="isInvalid('message')">{{ errorMessage('message') }}</div>
        </div>
        <!-- END: Text Message -->
        <!-- BEGIN: Text Message -->
        <div class="col-span-12 lg:col-span-6 input-form">
          <label for="message" class="form-label text-s text-slate-500">Preview</label>
          
          <!-- Media Preview (if exists) -->
          <div v-if="previewUrls.length > 0" class="mb-3">
            <div class="flex flex-wrap items-center gap-2">
              <div v-for="(preview, index) in previewUrls" :key="index" class="relative">
                <img 
                  :src="preview.url" 
                  :alt="preview.name"
                  class="h-20 w-20 object-cover rounded-lg border border-gray-200"
                />
              </div>
            </div>
          </div>
          
          <textarea name="message" id="preview" rows="6" cols="50" type="text" class="input-field w-full" disabled>{{ previewMessage }}</textarea>
          <span class="text-xs">The SenderID and Opt Out appears on the first message that is sent out – example:
            {{prepended_text}}You had inquired about health insurance. How can I help?{{appended_text}}
          </span>
          
          <div v-if="previewUrls.length > 0" class="mt-2">
            <span class="text-xs text-gray-500 block">MMS messages may incur additional charges and require compatible phone numbers.</span>
          </div>
        </div>
        <!-- END: Text Message -->
      </div>

      <!-- BEGIN: Location -->
      <div class="col-span-12 input-form" :class="{'has-error': isInvalid('location')}">
        <label for="location" class="form-label text-gray-600">Location</label>
        <select v-model="formData.location" class="input-field" name="" id="">
          <option value="B">Both</option>
          <option value="A">Action Wizard</option>
          <option value="T">Toolbelt</option>
        </select>
        <div class="error-message mt-1" v-if="isInvalid('location')">{{ errorMessage('location') }}</div>
      </div>
      <!-- END: Location -->

      <!-- BEGIN: Merge fields -->
      <div class="col-span-12 input-form">
        <label for="tooltip" class="form-label text-s text-gray-600 mb-2">Available Merge Fields</label>
        <p class="text-sm text-gray-600 box-border rounded-lg h-35 w-45 p-2 shadow border border-gray-300">
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[firstname]</span> - The Lead's First Name<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[lastname]</span> - The Lead's Last Name<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[state]</span> - The Lead's State<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[zip]</span> - The Lead's Zip Code<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[website]</span> - The Lead's Website<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[agentfirst]</span> - The Agent's First Name<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[agentlast]</span> - The Agent's Last Name<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[agentphone]</span> - The Agent's Phone Number<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[calendar_sharing_link]</span> - The Agent's Calendar Sharing Link<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[leaddob]</span> - The Lead's Date of Birth<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[leadage]</span> - The Lead's Age<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[spousefirstname]</span> - The Lead's Spouse First Name<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[spouselastname]</span> - The Lead's Spouse Last Name<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[spousedob]</span> - The Lead's Spouse Date of Birth<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[dependentdob1]</span> - The Lead's First Dependent Date of Birth<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[dependentdob2]</span> - The Lead's Second Dependent Date of Birth<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[dependentdob3]</span> - The Lead's Third Dependent Date of Birth<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[dependentdob4]</span> - The Lead's Fourth Dependent Date of Birth<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[dependentdob5]</span> - The Lead's Fifth Dependent Date of Birth<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[nextactionstamp]</span> - Date/Time of Next Action<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[nextactiondate]</span> - Date of Next Action<br>
            <span draggable="true" class="cursor-move draggable inline-block btn-secondary text-sm py-1 px-2 mb-1">[nextactiontime]</span> - Time of Next Action<br>
        </p>
      </div>
      <!-- END: Merge fields -->
    </div>

    <div class="text-right mt-5">
      <button type="submit" class="btn btn-primary w-24" :class="{'pointer-events-none': loading}">
        <LoadingSpinner v-if="loading"></LoadingSpinner>
        <span v-else>Submit</span>
      </button>
    </div>
  </form>
</template>
